<script setup lang="ts">
import { computed } from 'vue'
import RoomieTag from '@lahaus-roomie/roomie/src/components/RoomieTag/index.vue'
import RoomieIcon from '@lahaus-roomie/roomie/src/components/RoomieIcon/index.vue'
import RoomieButton from '@lahaus-roomie/roomie/src/components/RoomieButton/index.vue'
import AIIcon from '@lahaus-roomie/roomie/src/assets/icons/v2/ai.svg'
import VerifiedIcon from '@lahaus-roomie/roomie/src/assets/icons/v2/verified.svg'
import NewWindowIcon from '@lahaus-roomie/roomie/src/assets/icons/v2/new-window.svg'
import { getReferralBonus } from '@/utils/referralBonus'

const { $useLhId } = useNuxtApp()
const emit = defineEmits(['register-action'])

const { locale } = useI18n()
const { t, tm, rt } = useI18n()

const bonus = computed(() => getReferralBonus(locale.value, false))
const bonusWithCurrency = computed(() => getReferralBonus(locale.value, true))
const formInformation = tm('form_information')
</script>

<script lang="ts">
export default {
  name: 'ReferralProgramInformation'
}
</script>

<template>
  <div class="referral-program-information">
    <div class="mb-48 md:mb-40">
      <div class="referral-program-information__tag">
        <ClientOnly>
          <RoomieTag
            id="la-haus-verified-tag"
            variant="information"
            size="lg">
            <template #content>
              <span class="display-7 mr-4">
                {{ t('title_tag.1') }}
              </span>

              <span class="display-regular-7">
                {{ t('title_tag.2') }}
              </span>
            </template>
          </RoomieTag>
        </ClientOnly>
      </div>

      <h1 class="flex flex-col justify-center">
        <span class="">
          <span class="display-5 md:display-3">{{ t('title.1', { bonus }) }}</span>

          <span class="ml-8 display-6 md:display-4">{{ t('title.2') }}</span>
        </span>

        <span class="display-5 md:display-3">
          {{ t('title.3') }}
        </span>
      </h1>
    </div>

    <div class="referral-program-information__grid">
      <div class="referral-program-information__card">
        <div class="referral-program-information__content--col1">
          <h2 class="referral-program-information__title">
            {{ t('slides.1.title') }}
          </h2>

          <p class="referral-program-information__description">
            {{ t('slides.1.description') }}
          </p>
        </div>

        <div class="referral-program-information__content--col2">
          <div class="flex flex-col gap-8">
            <div
              v-for="(item, index) in formInformation"
              :key="index + rt(item)"
              class="bg-gray2-400 rounded-md flex items-center justify-between py-8 px-16 min-h-[5rem]">
              <span
                v-sanitize="rt(item)"
                class="body-2-m" />

              <RoomieIcon custom-styles="w-24 h-24 text-spring-600">
                <VerifiedIcon />
              </RoomieIcon>
            </div>
          </div>
        </div>
      </div>

      <div class="referral-program-information__card">
        <div class="referral-program-information__content--col1">
          <h2 class="referral-program-information__title">
            {{ t('slides.2.title') }}
          </h2>

          <p class="referral-program-information__description">
            {{ t('slides.2.description') }}
          </p>
        </div>

        <div class="referral-program-information__content--col2">
          <div class="relative">
            <img
              src="https://media.lahaus.com/statics/static/images/referral-program/share-form.png?width=289&height=165&fit=cover"
              alt="Comparte y gana"
              class="rounded-[1.6rem]">

            <div class="flex justify-center items-center absolute right-[1.2rem] md:right-0 top-[-1.4rem] bg-lime-600 p-lh-16 rounded-[1.4rem] w-[6.3rem] h-[4.9rem]">
              <div class="px-6">
                <RoomieIcon custom-styles="block text-pine-600 w-20 h-20">
                  <NewWindowIcon />
                </RoomieIcon>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="referral-program-information__card">
        <div class="referral-program-information__content--col1">
          <h2 class="referral-program-information__title">
            {{ t('slides.3.title') }}
          </h2>

          <p class="referral-program-information__description">
            {{ t('slides.3.description', { bonusWithCurrency }) }}
          </p>
        </div>

        <div class="referral-program-information__content--col2">
          <div class="flex justify-center items-center bg-gray2-400 w-full h-[16.4rem] rounded-[1.6rem]">
            <div class="relative w-full">
              <div class="referral-program-information__price-content -top-20 w-[12rem]  bg-pine-200" />

              <div class="-top-16 w-[15rem]  bg-pine-300 referral-program-information__price-content" />

              <div class="-top-12 w-[19rem]  bg-pine-500 referral-program-information__price-content" />

              <div class="-top-8 w-[22rem]  bg-pine-600 referral-program-information__price-content px-lh-8">
                <div class="w-full h-full flex justify-center items-center gap-4 text-white">
                  <span class="flex flex-row items-baseline gap-4">
                    <span class="display-7">
                      {{ t('slides.3.price', { bonus }) }}
                    </span>

                    <span class="body-1-sb">
                      {{ t('slides.3.currency') }}
                    </span>
                  </span>

                  <RoomieIcon custom-styles="text-lime-600 w-20 h-20">
                    <AIIcon />
                  </RoomieIcon>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="flex justify-center mt-48 md:mt-56">
      <RoomieButton
        id="referral-program-button-register-now"
        :data-lh-id="$useLhId({ P: 'home', Z: 'lahaus_referidos', A: 'redireccionar', C: t('register_now') })"
        :aria-label="t('register_now')"
        variant="primary"
        @click="emit('register-action')">
        {{ t('register_now') }}
      </RoomieButton>
    </div>
  </div>
</template>

<style src="./_index.scss" lang="scss"></style>
<i18n src="./i18n.json" lang="json" />
